import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCachedUser, isAuthenticated } from '../utils/auth';
import { showToast } from './ToastContainer';
import { supabase } from '../utils/supabaseClient';
import { Skeleton, TableRowSkeleton } from './common/Skeleton';
import { playBoostingRequestSound } from '../utils/notificationSound';
import '../styles/BoostingManagement.css';
import { getBoostingSubscriptionServiceTypes } from '../config/boostingSubscriptionServiceTypes';
import { GAME_SEARCH_ALIASES } from '../config/gameSearchAliases';
import { matchesGame, sortGamesBySearchRelevance } from '../utils/gameCatalogSearch';
import { getGameIcon as resolveGameIcon } from '../utils/getGameIcon';

function formatBoostingRequestCategory(gameSlug, category) {
  if (category == null || category === '') return 'Rank Boost';
  const cat = String(category);
  const types = getBoostingSubscriptionServiceTypes(gameSlug);
  const found = types.find((t) => t.id === cat || t.id === cat.toLowerCase());
  if (found) return found.label;
  const lower = cat.toLowerCase();
  if (lower === 'custom' || lower === 'custom request') return 'Custom Request';
  return cat.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatGameDisplayName(gameSlug, gamesList = []) {
  if (!gameSlug) return 'Unknown';
  const found = gamesList.find((g) => g.slug === gameSlug);
  if (found) return found.name;
  return String(gameSlug)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRequestStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  const map = {
    waiting: 'Waiting',
    'in-progress': 'In progress',
    submitted: 'Submitted',
    won: 'Completed',
    completed: 'Completed',
    canceled: 'Canceled',
    cancelled: 'Canceled',
    disputed: 'Disputed',
    lost: 'Lost',
  };
  return map[s] || (status ? String(status).replace(/-/g, ' ') : 'Unknown');
}

function requestStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'canceled' || s === 'cancelled' || s === 'lost') return 'canceled';
  if (s === 'won' || s === 'completed') return 'completed';
  if (s === 'disputed') return 'disputed';
  if (s === 'submitted') return 'submitted';
  if (s === 'waiting' || s === 'in-progress') return 'pending';
  return 'pending';
}

const BoostingManagement = ({ initialTab = 'received' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCachedUser();
  
  // Boosters bid on incoming requests; buyers only ever manage their own.
  const isSeller = Boolean(user?.is_seller);

  // Determine active tab from URL
  const getActiveTabFromPath = () => {
    if (!isSeller) return 'my-requests';
    if (location.pathname.includes('/boosting/my-requests')) {
      return 'my-requests';
    }
    if (location.pathname.includes('/boosting/received')) {
      return 'received';
    }
    return initialTab;
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromPath()); // 'my-requests' or 'received'
  
  // Sync with URL changes
  useEffect(() => {
    const tabFromPath = getActiveTabFromPath();
    if (tabFromPath !== activeTab) {
      setActiveTab(tabFromPath);
    }
  }, [location.pathname]);
  // Load cached subscriptions on mount
  const getCachedSubscriptions = () => {
    try {
      const cached = localStorage.getItem('booster_subscriptions');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is less than 5 minutes old
        if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          return parsed.data || [];
        }
      }
    } catch (e) {
      console.error('Failed to load cached subscriptions:', e);
    }
    return [];
  };

  const cachedSubscriptions = getCachedSubscriptions();
  const [subscriptions, setSubscriptions] = useState(cachedSubscriptions);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(cachedSubscriptions.length === 0);
  const [showManageSubscriptions, setShowManageSubscriptions] = useState(false);
  const [subscriptionSearchQuery, setSubscriptionSearchQuery] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all'); // 'all', 'subscribed', 'not-subscribed'
  const subscriptionsPanelRef = useRef(null);
  const subscriptionsScrollRef = useRef(null);

  const [expandedGames, setExpandedGames] = useState(() => new Set());

  useEffect(() => {
    if (!showManageSubscriptions) return;
    subscriptionsScrollRef.current?.scrollTo({ top: 0 });
    subscriptionsPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    // Expand games that already have services on
    const activeSlugs = allGames
      .filter((game) => {
        const types = getBoostingSubscriptionServiceTypes(game.slug);
        const sub = subscriptions.find((s) => s.game === game.slug);
        if (!sub?.enabled && !(sub?.service_types?.length > 0)) return false;
        return types.some((t) => sub?.service_types?.includes(t.id));
      })
      .map((g) => g.slug);
    setExpandedGames(new Set(activeSlugs));
  }, [showManageSubscriptions]);

  const toggleGameExpanded = (slug) => {
    setExpandedGames((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const getEnabledServiceCount = (gameSlug) => {
    const types = getBoostingSubscriptionServiceTypes(gameSlug);
    const subscription = subscriptions.find((sub) => sub.game === gameSlug);
    if (!subscription?.service_types?.length) return 0;
    return types.filter((t) => subscription.service_types.includes(t.id)).length;
  };
  
  // Received requests (as seller)
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [selectedGameFilter, setSelectedGameFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('waiting');
  
  // My requests (as buyer)
  const [myRequests, setMyRequests] = useState([]);
  const [loadingMyRequests, setLoadingMyRequests] = useState(true);
  const [myRequestsStatusFilter, setMyRequestsStatusFilter] = useState('all');
  
  // All available games with icons
  const allGames = [
    { slug: 'anime-vanguards', name: 'Anime Vanguards', icon: null },
    { slug: 'apex-legends', name: 'Apex Legends', icon: 'ApexLegends.png' },
    { slug: 'arc-raiders', name: 'Arc Raiders', icon: 'ARcraiders.png' },
    { slug: 'arena-breakout', name: 'Arena Breakout', icon: null },
    { slug: 'arena-breakout-infinite', name: 'Arena Breakout: Infinite', icon: null },
    { slug: 'battlefield', name: 'Battlefield', icon: null },
    { slug: 'black-desert-online', name: 'Black Desert Online', icon: null },
    { slug: 'blox-fruits', name: 'Blox Fruits', icon: null },
    { slug: 'brawl-stars', name: 'Brawl Stars', icon: null },
    { slug: 'cod-call-of-duty', name: 'Call of Duty', icon: 'Callofduty.png' },
    { slug: 'clash-of-clans', name: 'Clash of Clans', icon: null },
    { slug: 'clash-royale', name: 'Clash Royale', icon: 'Clashroyale.png' },
    { slug: 'counter-strike-2', name: 'Counter-Strike 2', icon: 'CS2.png' },
    { slug: 'dead-by-daylight', name: 'Dead By Daylight', icon: null },
    { slug: 'delta-force', name: 'Delta Force', icon: null },
    { slug: 'destiny-2', name: 'Destiny 2', icon: null },
    { slug: 'diablo-4', name: 'Diablo 4', icon: null },
    { slug: 'donutsmp', name: 'DonutSMP', icon: null },
    { slug: 'dota-2', name: 'Dota 2', icon: null },
    { slug: 'ea-sports-fc', name: 'EA Sports FC', icon: 'EAsports.png' },
    { slug: 'elder-scrolls-online', name: 'Elder Scrolls Online', icon: null },
    { slug: 'escape-from-tarkov', name: 'Escape from Tarkov', icon: 'Escapefromtarkov.png' },
    { slug: 'final-fantasy-xiv', name: 'Final Fantasy XIV', icon: null },
    { slug: 'fisch', name: 'Fisch', icon: null },
    { slug: 'fortnite', name: 'Fortnite', icon: 'Fortnite.png' },
    { slug: 'genshin-impact', name: 'Genshin Impact', icon: null },
    { slug: 'league-of-legends', name: 'League of Legends', icon: 'Leagueoflegends.png' },
    { slug: 'league-of-legends-wild-rift', name: 'League of Legends: Wild Rift', icon: null },
    { slug: 'marvel-rivals', name: 'Marvel Rivals', icon: null },
    { slug: 'mobile-legends', name: 'Mobile Legends', icon: null },
    { slug: 'new-world', name: 'New World', icon: null },
    { slug: 'old-school-runescape', name: 'Old School RuneScape', icon: 'Oldschoolrunescape.png' },
    { slug: 'overwatch-2', name: 'Overwatch 2', icon: null },
    { slug: 'path-of-exile', name: 'Path of Exile', icon: null },
    { slug: 'path-of-exile-2', name: 'Path of Exile 2', icon: null },
    { slug: 'pokemon-go', name: 'Pokemon Go', icon: null },
    { slug: 'rainbow-six-siege-x', name: 'Rainbow Six Siege X', icon: null },
    { slug: 'valorant', name: 'Valorant', icon: 'Valorant.png' },
    { slug: 'roblox', name: 'Roblox', icon: 'Roblox.png' },
    { slug: 'rocket-league', name: 'Rocket League', icon: 'rocketleague.png' },
    { slug: 'runescape-3', name: 'RuneScape 3', icon: null },
    { slug: 'rust', name: 'Rust', icon: null },
    { slug: 'the-first-descendant', name: 'The First Descendant', icon: null },
    { slug: 'the-forge', name: 'The Forge', icon: null },
    { slug: 'throne-and-liberty', name: 'Throne and Liberty', icon: null },
    { slug: 'warframe', name: 'Warframe', icon: null },
    { slug: 'where-winds-meet', name: 'Where Winds Meet', icon: null },
    { slug: 'warcraft', name: 'Warcraft', icon: 'Worldofwarcraft.png' },
    { slug: 'world-of-warcraft', name: 'World of Warcraft', icon: 'Worldofwarcraft.png' },
    { slug: 'wow-classic', name: 'WoW Classic', icon: null },
    { slug: 'wow-mists-of-pandaria', name: 'WoW Mists of Pandaria', icon: null },
  ];

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/auth?signin=true');
      return;
    }

    // Only show loading if we don't have cached data
    const hasCached = subscriptions.length > 0;
    if (!hasCached) {
      loadSubscriptions(true);
    } else {
      // Load fresh data in background
      loadSubscriptions(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'received') {
      loadRequests();
    } else if (activeTab === 'my-requests') {
      loadMyRequests();
    }
  }, [activeTab, selectedGameFilter, selectedStatusFilter, myRequestsStatusFilter]);

  // Subscribe to realtime updates for requests
  useEffect(() => {
    if (activeTab !== 'received' && activeTab !== 'my-requests') return;

    const channel = supabase
      .channel('boosting_requests_realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'boosting_requests',
      }, (payload) => {
        // Play sound notification when a new boosting request is created
        if (activeTab === 'received') {
          playBoostingRequestSound();
          loadRequests();
        } else if (activeTab === 'my-requests') {
          loadMyRequests();
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'boosting_requests',
      }, () => {
        if (activeTab === 'received') {
          loadRequests();
        } else if (activeTab === 'my-requests') {
          loadMyRequests();
        }
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'boosting_offers',
      }, () => {
        if (activeTab === 'received') {
          loadRequests();
        } else if (activeTab === 'my-requests') {
          loadMyRequests();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeTab]);

  const loadMyRequests = async () => {
    try {
      setLoadingMyRequests(true);
      const token = localStorage.getItem('auth_token');
      const statusParam = myRequestsStatusFilter !== 'all' ? `&status=${myRequestsStatusFilter}` : '';
      const response = await fetch(`/api/boosting/my-requests?${statusParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setMyRequests(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load my requests:', error);
      setMyRequests([]);
    } finally {
      setLoadingMyRequests(false);
    }
  };

  const loadSubscriptions = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoadingSubscriptions(true);
      }
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/boosting/subscriptions', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const subscriptionsData = result.data || [];
        setSubscriptions(subscriptionsData);
        // Cache subscriptions
        try {
          localStorage.setItem('booster_subscriptions', JSON.stringify({
            data: subscriptionsData,
            timestamp: Date.now(),
          }));
        } catch (e) {
          console.error('Failed to cache subscriptions:', e);
        }
      }
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
    } finally {
      if (showLoading) {
        setLoadingSubscriptions(false);
      }
    }
  };

  const loadRequests = async () => {
    try {
      setLoadingRequests(true);
      const token = localStorage.getItem('auth_token');
      const gameParam = selectedGameFilter !== 'all' ? `&game=${selectedGameFilter}` : '';
      const response = await fetch(`/api/boosting/orders?status=${selectedStatusFilter}${gameParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setRequests(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load requests:', error);
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  const toggleServiceType = async (gameSlug, serviceTypeId, enabled) => {
    try {
      const token = localStorage.getItem('auth_token');
      const existing = subscriptions.find(sub => sub.game === gameSlug);

      // Optimistically update the UI immediately
      setSubscriptions(prevSubs => {
        const newSubs = [...prevSubs];
        const subIndex = newSubs.findIndex(sub => sub.game === gameSlug);
        
        if (existing) {
          // Update existing subscription
          const currentServiceTypes = existing.service_types || [];
          let newServiceTypes;
          
          if (enabled) {
            newServiceTypes = currentServiceTypes.includes(serviceTypeId)
              ? currentServiceTypes
              : [...currentServiceTypes, serviceTypeId];
          } else {
            newServiceTypes = currentServiceTypes.filter(st => st !== serviceTypeId);
          }
          
          if (subIndex >= 0) {
            newSubs[subIndex] = {
              ...existing,
              service_types: newServiceTypes,
              enabled: newServiceTypes.length > 0,
            };
          }
        } else if (enabled) {
          // Create new subscription
          newSubs.push({
            id: `temp-${Date.now()}`, // Temporary ID
            user_id: user?.id,
            game: gameSlug,
            enabled: true,
            service_types: [serviceTypeId],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
        
        return newSubs;
      });

      if (existing) {
        // Update existing subscription with service type
        const response = await fetch('/api/boosting/subscriptions', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            id: existing.id,
            service_type: serviceTypeId,
            enabled,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          // Update with server response
          setSubscriptions(prevSubs => {
            const newSubs = [...prevSubs];
            const subIndex = newSubs.findIndex(sub => sub.game === gameSlug);
            if (subIndex >= 0 && result.data) {
              newSubs[subIndex] = result.data;
            }
            return newSubs;
          });
          // Update cache
          try {
            const updatedSubs = subscriptions.map(sub => 
              sub.game === gameSlug ? (result.data || sub) : sub
            );
            localStorage.setItem('booster_subscriptions', JSON.stringify({
              data: updatedSubs,
              timestamp: Date.now(),
            }));
          } catch (e) {
            console.error('Failed to cache subscriptions:', e);
          }
          
          const gameName = allGames.find(g => g.slug === gameSlug)?.name || gameSlug;
          const serviceLabel = getGameServiceTypes(gameSlug).find(st => st.id === serviceTypeId)?.label || serviceTypeId;
          showToast(
            enabled 
              ? `Enabled ${serviceLabel} for ${gameName}` 
              : `Disabled ${serviceLabel} for ${gameName}`, 
            'success'
          );
        } else {
          await loadSubscriptions(false);
          showToast('Failed to update subscription', 'error');
        }
      } else {
        // Create new subscription with this service type
        const response = await fetch('/api/boosting/subscriptions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            game: gameSlug,
            enabled: true,
            service_types: [serviceTypeId],
          }),
        });

        if (response.ok) {
          const result = await response.json();
          // Update with server response
          setSubscriptions(prevSubs => {
            const newSubs = [...prevSubs];
            const tempIndex = newSubs.findIndex(sub => sub.id?.toString().startsWith('temp-'));
            if (tempIndex >= 0 && result.data) {
              newSubs[tempIndex] = result.data;
            } else if (result.data) {
              newSubs.push(result.data);
            }
            return newSubs;
          });
          // Update cache
          try {
            const updatedSubs = [...subscriptions];
            const tempIndex = updatedSubs.findIndex(sub => sub.id?.toString().startsWith('temp-'));
            if (tempIndex >= 0 && result.data) {
              updatedSubs[tempIndex] = result.data;
            } else if (result.data) {
              updatedSubs.push(result.data);
            }
            localStorage.setItem('booster_subscriptions', JSON.stringify({
              data: updatedSubs,
              timestamp: Date.now(),
            }));
          } catch (e) {
            console.error('Failed to cache subscriptions:', e);
          }
          
          const gameName = allGames.find(g => g.slug === gameSlug)?.name || gameSlug;
          const serviceLabel = getGameServiceTypes(gameSlug).find(st => st.id === serviceTypeId)?.label || serviceTypeId;
          showToast(`Enabled ${serviceLabel} for ${gameName}`, 'success');
        } else {
          await loadSubscriptions(false);
          showToast('Failed to create subscription', 'error');
        }
      }
    } catch (error) {
      console.error('Failed to toggle service type:', error);
      await loadSubscriptions(false);
      showToast('Failed to update subscription', 'error');
    }
  };

  const getGameIcon = (iconName, gameName = null) => {
    if (!iconName && !gameName) return null;

    // First, use shared resolver used across the app.
    if (gameName) {
      const resolved = resolveGameIcon(gameName);
      if (resolved) return resolved;
    }

    // Fallback to iconName in navbaricons folder
    if (iconName) {
      return `/main/hero section/promos/navbaricons/${iconName}`;
    }
    
    return null;
  };

  const getSubscriptionStatus = (gameSlug) => getEnabledServiceCount(gameSlug) > 0;

  const getServiceTypeStatus = (gameSlug, serviceTypeId) => {
    const subscription = subscriptions.find(sub => sub.game === gameSlug);
    if (!subscription || !subscription.service_types) return false;
    return subscription.service_types.includes(serviceTypeId);
  };

  const getGameServiceTypes = (gameSlug) =>
    getBoostingSubscriptionServiceTypes(gameSlug);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const resolveRequestIcon = (request) => {
    if (request?.game_icon) return request.game_icon;
    const label = formatGameDisplayName(request?.game, allGames);
    return resolveGameIcon(label) || getGameIcon(null, label);
  };

  const renderGameCell = (request) => {
    const label = formatGameDisplayName(request.game, allGames);
    const icon = resolveRequestIcon(request);
    const category = formatBoostingRequestCategory(request.game, request.category);
    return (
      <div className="request-name-cell">
        <div className="request-game-icon-wrap">
          {icon ? (
            <img src={icon} alt="" className="request-game-icon" loading="lazy" />
          ) : (
            <span className="request-game-icon-fallback">{label.charAt(0)}</span>
          )}
        </div>
        <div className="request-name-text">
          <span className="request-name-primary">{label}</span>
          <span className="request-name-secondary">{category}</span>
        </div>
      </div>
    );
  };

  const renderStatusBadge = (status) => (
    <span className={`request-status-badge ${requestStatusClass(status)}`}>
      {formatRequestStatusLabel(status)}
    </span>
  );

  // Buyer and booster share one request page; the page itself adapts per role.
  const openRequestPage = (request) => {
    navigate(`/boosting/requests/${request.id}`);
  };

  const openOfferForm = (request) => {
    navigate(`/boosting/requests/${request.id}/offer`);
  };

  // Filter games based on search and subscription filter (same catalog rules as header search)
  const filteredGames = useMemo(() => {
    const q = subscriptionSearchQuery.toLowerCase().trim();
    let list = allGames.filter((game) => {
      const matchesSearch = !q || matchesGame(game.name, q, GAME_SEARCH_ALIASES);
      if (!matchesSearch) return false;
      if (subscriptionFilter === 'subscribed') {
        return getSubscriptionStatus(game.slug);
      }
      if (subscriptionFilter === 'not-subscribed') {
        return !getSubscriptionStatus(game.slug);
      }
      return true;
    });
    if (q) {
      const order = sortGamesBySearchRelevance(
        list.map((g) => g.name),
        q,
        GAME_SEARCH_ALIASES
      );
      const rank = new Map(order.map((name, idx) => [name, idx]));
      list = [...list].sort(
        (a, b) => (rank.get(a.name) ?? 999) - (rank.get(b.name) ?? 999)
      );
    }
    return list;
  }, [subscriptionSearchQuery, subscriptionFilter, subscriptions]);

  // Group games by subscription status
  const subscribedGames = allGames.filter(game => getSubscriptionStatus(game.slug));

  return (
    <div className="boosting-management">
      {!showManageSubscriptions && (
        <div className="boosting-toolbar">
          <div className="boosting-toolbar-left" />
          <div className="boosting-toolbar-actions">
            <button
              type="button"
              className="boost-link-btn"
              onClick={() => window.open('https://help.bazaar.gg', '_blank')}
            >
              How does it work?
            </button>
            {isSeller && (
              <button
                type="button"
                className="boost-link-btn boost-link-btn--accent"
                aria-expanded={false}
                onClick={() => setShowManageSubscriptions(true)}
              >
                Manage subscriptions
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`boosting-management-wrapper${showManageSubscriptions ? ' subscriptions-focus' : ''}`}
      >
        {showManageSubscriptions && (
          <div className="subscriptions-panel" ref={subscriptionsPanelRef}>
            <div className="subscriptions-panel-bar">
              <div className="subscriptions-panel-titles">
                <h3>Start selling</h3>
                <p>Turn on services you offer. Matching requests will notify you.</p>
              </div>
            </div>

            <div className="subscriptions-panel-actions">
              <button
                type="button"
                className="boost-link-btn"
                onClick={() => window.open('https://help.bazaar.gg', '_blank')}
              >
                How does it work?
              </button>
              <button
                type="button"
                className="boost-link-btn"
                aria-expanded={true}
                onClick={() => setShowManageSubscriptions(false)}
              >
                Hide subscriptions
              </button>
            </div>

            <div className="subscriptions-tools">
              <input
                type="search"
                placeholder="Search games..."
                value={subscriptionSearchQuery}
                onChange={(e) => setSubscriptionSearchQuery(e.target.value)}
                className="subscriptions-search-input"
                aria-label="Search games"
              />
              <div className="filter-buttons" role="tablist" aria-label="Subscription filter">
                <button
                  type="button"
                  className={`filter-btn ${subscriptionFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setSubscriptionFilter('all')}
                >
                  All ({allGames.length})
                </button>
                <button
                  type="button"
                  className={`filter-btn ${subscriptionFilter === 'subscribed' ? 'active' : ''}`}
                  onClick={() => setSubscriptionFilter('subscribed')}
                >
                  Active ({subscribedGames.length})
                </button>
                <button
                  type="button"
                  className={`filter-btn ${subscriptionFilter === 'not-subscribed' ? 'active' : ''}`}
                  onClick={() => setSubscriptionFilter('not-subscribed')}
                >
                  Off ({allGames.length - subscribedGames.length})
                </button>
              </div>
            </div>

            <div className="subscriptions-list-scroll" ref={subscriptionsScrollRef}>
              {loadingSubscriptions ? (
                <div className="subscriptions-loading">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="subscription-item-skeleton">
                      <Skeleton width="32px" height="32px" style={{ borderRadius: '6px' }} />
                      <div className="subscription-item-skeleton-text">
                        <Skeleton style={{ width: 'min(180px, 60%)', height: '14px', borderRadius: '4px' }} />
                        <Skeleton style={{ width: '72px', height: '12px', borderRadius: '4px' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredGames.length === 0 ? (
                <div className="subscriptions-empty">
                  <p>No games found.</p>
                </div>
              ) : (
                <div className="subscriptions-game-list">
                  {filteredGames.map((game) => {
                    const iconPath = getGameIcon(game.icon, game.name);
                    const types = getGameServiceTypes(game.slug);
                    const enabledCount = getEnabledServiceCount(game.slug);
                    const isOpen = expandedGames.has(game.slug);
                    const isActive = enabledCount > 0;

                    return (
                      <div
                        key={game.slug}
                        className={`sub-game${isOpen ? ' is-open' : ''}${isActive ? ' is-active' : ''}`}
                      >
                        <button
                          type="button"
                          className="sub-game-row"
                          aria-expanded={isOpen}
                          onClick={() => toggleGameExpanded(game.slug)}
                        >
                          <div className="sub-game-main">
                            <div className={`sub-game-icon${iconPath ? '' : ' is-empty'}`}>
                              {iconPath ? (
                                <img src={iconPath} alt="" loading="lazy" />
                              ) : (
                                <span>{game.name.charAt(0)}</span>
                              )}
                            </div>
                            <span className="sub-game-name">{game.name}</span>
                          </div>
                          <div className="sub-game-aside">
                            <span className={`sub-game-badge${isActive ? ' on' : ''}`}>
                              {isActive ? `${enabledCount} on` : 'Off'}
                            </span>
                            <span className={`sub-game-chevron${isOpen ? ' open' : ''}`} aria-hidden>
                              ▾
                            </span>
                          </div>
                        </button>

                        {isOpen && (
                          <div className="sub-game-services">
                            {types.map((serviceType) => {
                              const isServiceEnabled = getServiceTypeStatus(
                                game.slug,
                                serviceType.id
                              );
                              return (
                                <div key={serviceType.id} className="service-toggle-row">
                                  <span
                                    className="service-toggle-label"
                                    id={`svc-label-${game.slug}-${serviceType.id}`}
                                  >
                                    {serviceType.label}
                                  </span>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={isServiceEnabled}
                                    aria-labelledby={`svc-label-${game.slug}-${serviceType.id}`}
                                    className={`service-switch${isServiceEnabled ? ' on' : ''}`}
                                    onClick={() =>
                                      toggleServiceType(
                                        game.slug,
                                        serviceType.id,
                                        !isServiceEnabled
                                      )
                                    }
                                  >
                                    <span className="service-switch-thumb" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {!showManageSubscriptions && (
        <div className="boosting-content">
        {activeTab === 'my-requests' ? (
          <div className="my-requests-section">
            <div className="requests-filters">
              <div className="filter-buttons" role="tablist" aria-label="Request status">
                <button
                  type="button"
                  className={`filter-btn ${myRequestsStatusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setMyRequestsStatusFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`filter-btn ${myRequestsStatusFilter === 'waiting' || myRequestsStatusFilter === 'in-progress' ? 'active' : ''}`}
                  onClick={() => setMyRequestsStatusFilter('waiting')}
                >
                  In progress
                </button>
                <button
                  type="button"
                  className={`filter-btn ${myRequestsStatusFilter === 'won' || myRequestsStatusFilter === 'completed' ? 'active' : ''}`}
                  onClick={() => setMyRequestsStatusFilter('won')}
                >
                  Completed
                </button>
                <button
                  type="button"
                  className={`filter-btn ${myRequestsStatusFilter === 'disputed' ? 'active' : ''}`}
                  onClick={() => setMyRequestsStatusFilter('disputed')}
                >
                  Disputed
                </button>
                <button
                  type="button"
                  className={`filter-btn ${myRequestsStatusFilter === 'canceled' ? 'active' : ''}`}
                  onClick={() => setMyRequestsStatusFilter('canceled')}
                >
                  Canceled
                </button>
              </div>
            </div>

            {loadingMyRequests ? (
              <div className="orders-table-shell requests-table-shell">
                <div className="orders-table-scroll">
                  <table className="requests-table">
                    <thead>
                      <tr>
                        <th>Request</th>
                        <th>Status</th>
                        <th>Offers</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3, 4, 5].map(i => (
                        <TableRowSkeleton key={i} columns={5} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : myRequests.length === 0 ? (
              <div className="requests-empty">
                <p>No boosting requests yet.</p>
                <p>
                  Open the Boosting tab on any game page and hit &quot;Get offers now&quot; — subscribed
                  boosters will send you competing offers.
                </p>
              </div>
            ) : (
              <>
                <div className="orders-table-shell requests-table-shell desktop-only">
                  <div className="orders-table-scroll">
                    <table className="requests-table">
                      <thead>
                        <tr>
                          <th>Request</th>
                          <th>Status</th>
                          <th>Offers</th>
                          <th>Created</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myRequests.map((request) => (
                          <tr
                            key={request.id}
                            className="requests-table-row"
                            onClick={() => openRequestPage(request)}
                          >
                            <td>{renderGameCell(request)}</td>
                            <td>{renderStatusBadge(request.status)}</td>
                            <td className="request-meta-cell">{request.offers_count || 0}</td>
                            <td className="request-date-cell">{formatDate(request.created_at)}</td>
                            <td>
                              <button
                                type="button"
                                className="request-inline-action"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openRequestPage(request);
                                }}
                              >
                                View offers
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="requests-cards mobile-only">
                  {myRequests.map((request) => (
                    <div
                      key={request.id}
                      className="request-card-mobile"
                      onClick={() => openRequestPage(request)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openRequestPage(request);
                        }
                      }}
                    >
                      {renderGameCell(request)}
                      <div className="request-card-meta-mobile">
                        {renderStatusBadge(request.status)}
                        <span className="request-card-offers">{request.offers_count || 0} offers</span>
                        <span className="request-card-date">{formatDate(request.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="received-requests-section">
            <div className="requests-filters">
              <select
                className="filter-select"
                value={selectedGameFilter}
                onChange={(e) => setSelectedGameFilter(e.target.value)}
                aria-label="Filter by game"
              >
                <option value="all">All games</option>
                {subscribedGames.map(game => (
                  <option key={game.slug} value={game.slug}>{game.name}</option>
                ))}
              </select>
              <div className="filter-buttons" role="tablist" aria-label="Offer status">
                <button
                  type="button"
                  className={`filter-btn ${selectedStatusFilter === 'waiting' ? 'active' : ''}`}
                  onClick={() => setSelectedStatusFilter('waiting')}
                >
                  Waiting
                </button>
                <button
                  type="button"
                  className={`filter-btn ${selectedStatusFilter === 'submitted' ? 'active' : ''}`}
                  onClick={() => setSelectedStatusFilter('submitted')}
                >
                  Submitted
                </button>
                <button
                  type="button"
                  className={`filter-btn ${selectedStatusFilter === 'won' ? 'active' : ''}`}
                  onClick={() => setSelectedStatusFilter('won')}
                >
                  Won
                </button>
                <button
                  type="button"
                  className={`filter-btn ${selectedStatusFilter === 'lost' ? 'active' : ''}`}
                  onClick={() => setSelectedStatusFilter('lost')}
                >
                  Lost
                </button>
              </div>
            </div>

            {loadingRequests ? (
              <div className="orders-table-shell requests-table-shell">
                <div className="orders-table-scroll">
                  <table className="requests-table">
                    <thead>
                      <tr>
                        <th>Request</th>
                        <th>Buyer</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3, 4, 5].map(i => (
                        <TableRowSkeleton key={i} columns={4} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : requests.length === 0 ? (
              <div className="requests-empty">
                <p>No requests found for this filter.</p>
              </div>
            ) : (
              <>
                <div className="orders-table-shell requests-table-shell desktop-only">
                  <div className="orders-table-scroll">
                    <table className="requests-table">
                      <thead>
                        <tr>
                          <th>Request</th>
                          <th>Buyer</th>
                          <th>Created</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((request) => (
                          <tr
                            key={request.id}
                            className="requests-table-row"
                            onClick={() => openRequestPage(request)}
                          >
                            <td>{renderGameCell(request)}</td>
                            <td className="request-meta-cell">{request.buyer_username || 'N/A'}</td>
                            <td className="request-date-cell">{formatDate(request.created_at)}</td>
                            <td>
                              {request.has_offer ? (
                                <span className="request-status-badge submitted">Viewed</span>
                              ) : (
                                <button
                                  type="button"
                                  className="request-inline-action"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openOfferForm(request);
                                  }}
                                >
                                  Create offer
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="requests-cards mobile-only">
                  {requests.map((request) => (
                    <div
                      key={request.id}
                      className="request-card-mobile"
                      onClick={() => openRequestPage(request)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openRequestPage(request);
                        }
                      }}
                    >
                      {renderGameCell(request)}
                      <div className="request-card-meta-mobile">
                        <span className="request-card-buyer">{request.buyer_username || 'N/A'}</span>
                        <span className="request-card-date">{formatDate(request.created_at)}</span>
                        {request.has_offer ? (
                          <span className="request-status-badge submitted">Viewed</span>
                        ) : (
                          <button
                            type="button"
                            className="request-inline-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              openOfferForm(request);
                            }}
                          >
                            Create offer
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        </div>
        )}
      </div>

    </div>
  );
};

export default BoostingManagement;
